'use client';

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Level2CategoryCard } from '../Level2CategoryCard';
import { Level2EmptyState } from '../Level2EmptyState';
import styles from './Level2ContentGrid.module.scss';

export interface ContentItem {
  id: string;
  title: string;
  description: string;
  href: string;
  icon?: string;
  tags?: string[];
  date?: string;
  image?: string;
  status?: 'active' | 'coming-soon' | 'new';
}

interface Level2ContentGridProps {
  items: ContentItem[];
  emptyMessage?: string;
  ctaButton?: {
    text: string;
    href: string;
  };
  onItemClick?: (item: ContentItem) => void;
  className?: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

export function Level2ContentGrid({
  items,
  emptyMessage,
  ctaButton,
  onItemClick,
  className
}: Level2ContentGridProps) {
  const [_visibleCards, setVisibleCards] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Intersection Observer for card visibility animation
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-id');
            if (id) {
              setVisibleCards((prev) => new Set(prev).add(id));
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const cards = document.querySelectorAll(`.${styles.contentCard}`);
    cards.forEach((card) => {
      observerRef.current?.observe(card);
    });

    return () => {
      cards.forEach((card) => {
        observerRef.current?.unobserve(card);
      });
    };
  }, [items]);

  if (items.length === 0) {
    return (
      <Level2EmptyState
        message={emptyMessage}
        ctaButton={ctaButton}
        className={className}
      />
    );
  }

  return (
    <div className={`${styles.mainContent} ${className || ''}`}>
      <div className={styles.container}>
        <motion.div 
          className={styles.contentGrid}
          variants={containerVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
        >
          <AnimatePresence>
            {items.map((item, index) => (
              <Level2CategoryCard
                key={item.id}
                item={item}
                index={index}
                onClick={onItemClick}
                className={styles.contentCard}
                data-id={item.id}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}

export default Level2ContentGrid;